'use client';
import { Dialog, Portal } from '@ark-ui/react';
import { usePathname, useRouter } from 'next/navigation';
import type { FC } from 'react';
import { useCallback } from 'react';
import { memo } from 'react';
import { routes } from '~/utils/routes';
import { Button } from '@app/_components/button';
import { css } from '@styled-system/css';
import { Paragraph, Image, styled, Box } from '@styled-system/jsx';
import icon from './assets/icon.svg';

export type Props = {
  isOpen: boolean;
  onClose?: () => void;
  onClickCancel: () => void;
  onClickOk: (showAlways: boolean) => void;
  isShowAlwaysChecked: boolean;
  changeAlwaysShowSetting: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isAnonymous?: boolean;
};

const _SensitiveStoryModal: FC<Props> = ({
  isOpen,
  onClose,
  onClickCancel,
  onClickOk,
  isAnonymous,
  isShowAlwaysChecked,
  changeAlwaysShowSetting,
}) => {
  const handleOnOpenChange = useCallback(
    ({ open }: { open: boolean }) => {
      if (!open) {
        onClose?.();
      }
    },
    [onClose]
  );

  const currentPath = usePathname();
  const router = useRouter();
  const handleOnClickLogin = useCallback(() => {
    router.push(routes.login.url(currentPath));
  }, [currentPath, router]);

  return (
    <Dialog.Root
      lazyMount
      unmountOnExit
      open={isOpen}
      onOpenChange={handleOnOpenChange}
      closeOnInteractOutside={false}
    >
      <Portal>
        <Backdrop />
        <Positioner>
          <Content minHeight="300px">
            <Image
              margin="22px auto 0 auto"
              src={icon}
              alt="センシティブな内容"
            />
            <Paragraph
              mt="20px"
              lineHeight="24px"
              fontSize="size_18"
              fontWeight="700"
            >
              注意：センシティブな内容
            </Paragraph>
            <Paragraph
              lineHeight="24px"
              fontSize="size_14"
              fontWeight="400"
              m="22px auto 0 auto"
              color="accent.700"
              maxWidth="60%"
              textAlign="left"
            >
              この作品は、一部の読者にとって不適切な内容を含んでいる可能性があります。
            </Paragraph>
            <Button
              maxWidth="90%"
              size="small"
              width="360px"
              height="50px"
              onClick={() => onClickOk(isShowAlwaysChecked)}
              backgroundColor="tint"
              color="white"
              fontSize="size_16"
              fontWeight="bold"
              margin="32px auto 16px auto"
              _focus={{
                outline: 0,
                outlineColor: 'transparent',
                outlineStyle: 'none',
              }}
            >
              理解した上で作品をみる
            </Button>

            <Button
              size="small"
              width="360px"
              height="50px"
              maxWidth="90%"
              onClick={onClickCancel}
              backgroundColor="white"
              color="accent.1000"
              fontSize="size_16"
              fontWeight="bold"
              margin="20px auto 32px auto"
              border="1px solid #313238"
              _focus={{
                outline: 0,
                outlineColor: 'transparent',
                outlineStyle: 'none',
              }}
            >
              戻る
            </Button>
            {isAnonymous ? (
              <Box>
                <span
                  onClick={handleOnClickLogin}
                  className={css({
                    color: 'tint',
                    cursor: 'pointer',
                    _hover: { opacity: 0.7 },
                  })}
                >
                  ログイン
                </span>
                してセンシティブ作品の自動表示設定
              </Box>
            ) : (
              <>
                <input
                  name="alwaysShowSetting"
                  type="checkbox"
                  checked={isShowAlwaysChecked}
                  onChange={changeAlwaysShowSetting}
                />
                <label
                  htmlFor="alwaysShowSetting"
                  className={css({
                    marginTop: '14px',
                    marginLeft: '8px',
                    marginBottom: '16px',
                  })}
                >
                  次回から自動的にセンシティブ作品を表示
                </label>
              </>
            )}
          </Content>
        </Positioner>
      </Portal>
    </Dialog.Root>
  );
};

const Backdrop = styled(Dialog.Backdrop, {
  base: {
    backdropFilter: 'blur(20px)',
    background: 'rgba(0, 0, 0, 0.5)',
    height: '100vh',
    left: '0',
    position: 'fixed',
    top: '0',
    width: '100vw',
    zIndex: 'modal_backdrop',
    _open: {
      animation: 'fadein 150ms ease-out',
    },
    _closed: {
      animation: 'fadeout 150ms ease-out',
    },
  },
});

const Positioner = styled(Dialog.Positioner, {
  base: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    position: 'fixed',
    top: {
      base: 0,
      lg: 24,
    },
    bottom: {
      base: 0,
      lg: 24,
    },
    left: {
      base: 0,
      lg: undefined,
    },
    right: {
      base: 0,
      lg: undefined,
    },
    margin: {
      base: 0,
      lg: 'auto',
    },
    height: {
      base: '100dvh',
      lg: '300px',
    },
    width: {
      base: '100dvw',
      lg: '580px',
    },
    zIndex: 'teller_modal',
  },
});

const Content = styled(Dialog.Content, {
  base: {
    background: 'white',
    boxShadow: 'lg',
    textAlign: 'center',
    borderRadius: {
      base: '8px',
    },
    height: 'auto',
    width: '90%',
    paddingBottom: '24px',
    _open: {
      animation: 'fadein 150ms ease-out',
    },
    _closed: {
      animation: 'fadeout 150ms ease-out',
    },
  },
});

export const SensitiveStoryModal = memo(_SensitiveStoryModal);
